import Notification from '@rio-cloud/rio-uikit/Notification';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/navigation/Tabs';
import type {
    CreateTeamInputFE,
    CreateUserInputFE,
    TeamSummaryFE,
    UpdateTeamInputFE,
    UpdateUserInputFE,
    UserFE,
} from 'domain/systemAdmin';
import SystemFleetsTable from 'features/settings/fleets/SystemFleetsTable';
import RoleInfoDialog from 'features/settings/RoleInfoDialog';
import TabContentHeader from 'features/settings/TabContentHeader';
import SystemTeamsTable from 'features/settings/teams/SystemTeamsTable';
import ExitActionDialog from 'features/settings/users/ExitActionDialog';
import SystemUsersTable from 'features/settings/users/SystemUsersTable';
import type { UserFormData } from 'features/settings/users/types';
import UserActionErrorDialog from 'features/settings/users/UserActionErrorDialog';
import UserDialog from 'features/settings/users/UserDialog';
import { SystemAdminDialogTypes, SystemAdminFormTypes } from 'helper/types';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useListSystemFleetsQuery } from 'services/systemAdmin/fleets';
import { useCreateUserMutation, useEditUserMutation } from 'services/user';
import {
    clearActiveElements,
    getTeamDialog,
    getUserDialog,
    getVehicleDialog,
    setErrorDialogOpen,
    setExitActionDialog,
    setExitTeamActionDialog,
    setSystemAdminOpenDialog,
    setTeamDialog,
    setTeamFormData,
    setUserDialog,
    setUserFormData,
    setVehicleDialog,
    toogleShowRoleDialog,
    type VehicleDialogState,
} from 'store/slices/settingsPageSlice';
import { setSystemFleetsFormType } from 'store/slices/systemFleet';

import './_styles.scss';

import ExitTeamActionDialog from 'features/settings/teams/ExitTeamActionDialog';
import TeamDialog from 'features/settings/teams/TeamDialog';
import type { TeamFormData } from 'features/settings/teams/types';
import {
    ContainerBackendCreationError,
    createVehicleWithContainerBackend,
} from 'features/settings/vehicles/createVehicleWithContainerBackend';
import {
    createContainerBackend,
    handleContainerBackendCreationError,
    handleVehicleCreationSuccess,
} from 'features/settings/vehicles/handleVehicleCreation';
import SystemVehiclesTable from 'features/settings/vehicles/SystemVehiclesTable';
import type { SimulatedVehicleCreation } from 'features/settings/vehicles/simulatedVehicleCreationNotifications';
import type { SimulationConfig } from 'features/settings/vehicles/vehicle-dialog/CreateVehicleForm';
import VehicleDialog, { type VehicleDialogSubmit } from 'features/settings/vehicles/vehicle-dialog/VehicleDialog';
import { useCan } from 'hooks/useCan';
import { useCreateTeamMutation, useEditTeamMutation } from 'services/systemAdmin/teams';
import { useCreateVehicleMutation, useUpdateVehicleMutation } from 'services/vehiclesV2';

type OpenVehicleDialogState = Extract<VehicleDialogState, { status: 'open' }>;

const SettingsPage = () => {
    const dispatch = useDispatch();
    const vehicleDialog = useSelector(getVehicleDialog);
    const [lastOpenVehicleDialog, setLastOpenVehicleDialog] = useState<OpenVehicleDialogState>({
        status: 'open',
        mode: 'create',
    });
    const { user: userDialogData } = useSelector(getUserDialog);
    const { team: teamDialogData } = useSelector(getTeamDialog);

    useEffect(() => {
        if (vehicleDialog.status === 'open') {
            setLastOpenVehicleDialog(vehicleDialog);
        }
    }, [vehicleDialog]);

    const renderedVehicleDialog = vehicleDialog.status === 'open' ? vehicleDialog : lastOpenVehicleDialog;

    const {
        data: fleets,
        isLoading: isLoadingFleets,
        isError: isErrorFleets,
        refetch: refetchFleets,
        isFetching: isFetchingFleets,
    } = useListSystemFleetsQuery('ListFleets', {
        refetchOnMountOrArgChange: true,
    });

    const [createUser, { isLoading: isCreating }] = useCreateUserMutation({
        fixedCacheKey: 'createUser',
    });
    const [editUser, { isLoading: isEditing }] = useEditUserMutation({
        fixedCacheKey: 'editUser',
    });

    const [createTeam, { isLoading: isCreatingTeam }] = useCreateTeamMutation({
        fixedCacheKey: 'createUser',
    });
    const [editTeam, { isLoading: isEditingTeam }] = useEditTeamMutation({
        fixedCacheKey: 'editUser',
    });
    const [createVehicle, { isLoading: isCreatingVehicle }] = useCreateVehicleMutation();
    const [updateVehicle, { isLoading: isUpdatingVehicle }] = useUpdateVehicleMutation();
    const [isCreatingContainerBackend, setIsCreatingContainerBackend] = useState(false);

    const toogleRoleDialog = () => {
        dispatch(toogleShowRoleDialog());
    };

    const openUserDialog = (user: UserFE | undefined) => {
        dispatch(setUserDialog({ user, showUserDialog: true }));
    };

    const openTeamDialog = (team: TeamSummaryFE | undefined) => {
        dispatch(setTeamDialog({ team, showTeamDialog: true }));
    };

    const closeSidebar = () => {
        dispatch(clearActiveElements());
    };

    const throwSuccessNotification = (user: any) => {
        if (userDialogData === undefined) {
            return Notification.success(
                <div>
                    <div className='margin-bottom-10'>
                        We sent the user an invitation e-mail to set up a password and log in the Control Center
                    </div>
                </div>,
                {
                    title: `${user?.data?.firstName} ${user?.data?.lastName} was added`,
                    timeout: 10_000,
                    closeButton: true,
                    hideProgressBar: false,
                }
            );
        }
        return Notification.success(<></>, {
            title: `${user?.data?.firstName} ${user?.data?.lastName} changes were saved`,
            timeout: 10_000,
            closeButton: true,
            hideProgressBar: false,
        });
    };

    const throwTeamSuccessNotification = (team: any) => {
        if (teamDialogData !== undefined && teamDialogData.id !== undefined) {
            return Notification.success(<></>, {
                title: `${team?.data?.name} team changes were saved`,
                timeout: 10_000,
                closeButton: true,
                hideProgressBar: false,
            });
        }
        return Notification.success(<></>, {
            title: `${team?.data?.name} team was created`,
            timeout: 10_000,
            closeButton: true,
            hideProgressBar: false,
        });
    };

    const handleConfirm = async (formData: UserFormData) => {
        let user;
        try {
            if (userDialogData === undefined) {
                const input: CreateUserInputFE = {
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    email: formData.email,
                    phone: formData.phone ?? '',
                    city: formData.city ?? '',
                    country: formData.country ?? '',
                    roles: formData.roles,
                    teams: formData.teams,
                };
                user = await createUser(input);
            } else {
                const input: UpdateUserInputFE = {
                    id: userDialogData.id,
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    phone: formData.phone ?? '',
                    city: formData.city ?? '',
                    country: formData.country ?? '',
                    roles: formData.roles,
                    teams: formData.teams,
                };
                user = await editUser(input);
            }
        } catch (e) {
            console.error(e);
        } finally {
            dispatch(setUserDialog({ user: undefined, showUserDialog: false }));
            dispatch(setUserFormData(undefined));
            closeSidebar();
            if (user?.error) {
                openErrorDialog();
            } else {
                throwSuccessNotification(user);
            }
        }
    };

    const handleTeamConfirm = async (formData: TeamFormData) => {
        let team;
        try {
            if (teamDialogData !== undefined && teamDialogData.id !== undefined) {
                const input: UpdateTeamInputFE = {
                    id: teamDialogData.id,
                    name: formData.name,
                    fleets: formData.fleets,
                    users: formData.users,
                };
                team = await editTeam(input);
            } else {
                const input: CreateTeamInputFE = {
                    name: formData.name,
                    fleets: formData.fleets,
                    users: formData.users,
                };
                team = await createTeam(input);
            }
        } catch (e) {
            console.error(e);
        } finally {
            dispatch(setTeamDialog({ team: undefined, showTeamDialog: false }));
            dispatch(setTeamFormData(undefined));
            closeSidebar();
            if (team?.error) {
                openErrorDialog();
            } else {
                throwTeamSuccessNotification(team);
            }
        }
    };

    const openCancelUserDialog = () => {
        dispatch(setExitActionDialog(true));
        dispatch(setUserDialog({ showUserDialog: false, user: undefined }));
    };

    const openCancelTeamDialog = () => {
        dispatch(setExitTeamActionDialog(true));
        dispatch(setTeamDialog({ showTeamDialog: false }));
    };

    const exitCreating = () => {
        dispatch(setExitActionDialog(false));
        dispatch(setUserDialog({ user: undefined, showUserDialog: false }));
        dispatch(setUserFormData(undefined));
    };

    const keepCreating = () => {
        dispatch(setExitActionDialog(false));
        dispatch(setUserDialog({ showUserDialog: true, user: undefined }));
    };

    const exitTeamCreating = () => {
        dispatch(setExitTeamActionDialog(false));
        dispatch(setTeamDialog({ team: undefined, showTeamDialog: false }));
        dispatch(setTeamFormData(undefined));
    };

    const keepTeamCreating = () => {
        dispatch(setExitTeamActionDialog(false));
        dispatch(setTeamDialog({ showTeamDialog: true }));
    };

    const openErrorDialog = () => {
        dispatch(setErrorDialogOpen(true));
    };

    const closeErrorDialog = () => {
        dispatch(setErrorDialogOpen(false));
    };

    const openCreateFleetDialog = () => {
        dispatch(setSystemAdminOpenDialog(SystemAdminDialogTypes.FLEET_DIALOG));
        dispatch(setSystemFleetsFormType(SystemAdminFormTypes.CREATE_FORM));
    };

    const handleVehicleSubmit = async (payload: VehicleDialogSubmit) => {
        if (payload.mode === 'update') {
            await updateVehicle({ vin: payload.vin, vehicle: payload.vehicle }).unwrap();
            dispatch(setVehicleDialog({ status: 'closed' }));
            Notification.success(<></>, {
                title: `${payload.vehicle.name} was updated`,
                timeout: 5_000,
                closeButton: true,
                hideProgressBar: false,
            });
            return;
        }

        const isConnectivitySimulation = payload.vehicle.subtype === 'SIMULATED_VEHICLE_WITH_CONNECTIVITY';
        const shouldCreateContainerBackend = isConnectivitySimulation && Boolean(payload.simulationConfig);

        const createBackend = (vin: string, simulationConfig: SimulationConfig) =>
            createContainerBackend({
                vin,
                name: payload.vehicle.name,
                externalId: payload.vehicle.externalId,
                simulationConfig,
            });

        if (shouldCreateContainerBackend) {
            setIsCreatingContainerBackend(true);
        }

        let simulatedVehicleCreation: SimulatedVehicleCreation | undefined;
        try {
            try {
                const result = await createVehicleWithContainerBackend({
                    vehicle: payload.vehicle,
                    simulationConfig: payload.simulationConfig,
                    createVehicle: vehicle => createVehicle(vehicle).unwrap(),
                    createContainerBackend: createBackend,
                });
                simulatedVehicleCreation = result.simulatedVehicleCreation;
            } catch (error) {
                if (error instanceof ContainerBackendCreationError) {
                    handleContainerBackendCreationError(error, {
                        vehicleName: payload.vehicle.name,
                        onCloseDialog: () => dispatch(setVehicleDialog({ status: 'closed' })),
                        createContainerBackend: createBackend,
                    });
                    return;
                }
                throw error;
            }
        } finally {
            if (shouldCreateContainerBackend) {
                setIsCreatingContainerBackend(false);
            }
        }

        dispatch(setVehicleDialog({ status: 'closed' }));
        handleVehicleCreationSuccess(simulatedVehicleCreation, payload.vehicle.name);
    };

    const { can: canCreateUsers } = useCan({ type: 'users', action: 'create' });
    const { can: canCreateTeams } = useCan({ type: 'teams', action: 'create' });
    const { can: canCreateFleets } = useCan({ type: 'fleets', action: 'create' });

    return (
        <main className='SettingsPage' data-testid='settings-page-content'>
            <RoleInfoDialog isLoading={false} isError={false} />
            <VehicleDialog
                show={vehicleDialog.status === 'open'}
                mode={renderedVehicleDialog.mode}
                vehicle={renderedVehicleDialog.mode === 'update' ? renderedVehicleDialog.vehicle : undefined}
                isSubmitting={
                    renderedVehicleDialog.mode === 'create'
                        ? isCreatingVehicle || isCreatingContainerBackend
                        : isUpdatingVehicle
                }
                onCancel={() => dispatch(setVehicleDialog({ status: 'closed' }))}
                onSubmit={handleVehicleSubmit}
            />
            <TeamDialog
                isLoading={false}
                isExecuting={isCreatingTeam || isEditingTeam}
                isError={false}
                onCancel={openCancelTeamDialog}
                onConfirm={handleTeamConfirm}
            />
            <UserDialog
                isLoading={false}
                isExecuting={isCreating || isEditing}
                isError={false}
                onCancel={openCancelUserDialog}
                onConfirm={handleConfirm}
            />
            <ExitActionDialog id='exit-user-action' onConfirm={exitCreating} onCancel={keepCreating} />
            <ExitTeamActionDialog id='exit-team-action' onConfirm={exitTeamCreating} onCancel={keepTeamCreating} />
            <UserActionErrorDialog onConfirm={closeErrorDialog} />
            <div>
                <h4 className='SettingsPage__header' data-testid='system-admin-page-header'>
                    Settings
                </h4>
            </div>
            <div className='SettingsPage__content'>
                <Tabs defaultValue='users-tab' orientation='vertical'>
                    <TabsList aria-label='vehicle detail tabs'>
                        <TabsTrigger data-testid='settings-tab-users' value='users-tab'>
                            <span data-testid='system-admin-tab-header-users'>Users</span>
                        </TabsTrigger>
                        <TabsTrigger value='teams-tab'>
                            <span data-testid='system-admin-tab-header-teams'>Teams</span>
                        </TabsTrigger>
                        <TabsTrigger value='vehicles-tab'>
                            <span data-testid='system-admin-tab-header-vehicles'>Vehicles</span>
                        </TabsTrigger>
                        <TabsTrigger data-testid='settings-tab-fleets' value='fleets-tab'>
                            <span data-testid='system-admin-tab-header-fleets'>Fleets</span>
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value='users-tab'>
                        <div className='SettingsPage__tabContent'>
                            <TabContentHeader
                                title='Users'
                                message='Manage user accesses and roles'
                                buttonLabel='Create new user'
                                buttonClick={() => openUserDialog(undefined)}
                                isButtonVisible={canCreateUsers}
                                iconClick={toogleRoleDialog}
                            />
                        </div>
                        <div>
                            <SystemUsersTable openDialogInfo={toogleRoleDialog} />
                        </div>
                    </TabsContent>
                    <TabsContent forceMount value='teams-tab'>
                        <div className='SettingsPage__tabContent'>
                            <TabContentHeader
                                title='Teams'
                                message='Manage user and fleet assignments'
                                buttonLabel='Create new team'
                                buttonClick={() => openTeamDialog(undefined)}
                                isButtonVisible={canCreateTeams}
                            />
                        </div>
                        <div>
                            <SystemTeamsTable />
                        </div>
                    </TabsContent>
                    <TabsContent value='vehicles-tab'>
                        <div data-testid='system-vehicles-tab-header' className='SettingsPage__tabContent'>
                            <TabContentHeader
                                title='Vehicles'
                                message='Create, edit and delete vehicles'
                                buttonLabel='Create new vehicle'
                                buttonClick={() => dispatch(setVehicleDialog({ status: 'open', mode: 'create' }))}
                                isButtonVisible={canCreateFleets}
                            />
                        </div>
                        <div data-testid='system-vehicles-tab-content'>
                            <SystemVehiclesTable />
                        </div>
                    </TabsContent>
                    <TabsContent forceMount value='fleets-tab'>
                        <div data-testid='system-fleets-tab-header' className='SettingsPage__tabContent'>
                            <TabContentHeader
                                title='Fleets'
                                message='Manage vehicle fleets'
                                buttonLabel='Create new fleet'
                                buttonClick={openCreateFleetDialog}
                                isButtonVisible={canCreateFleets}
                            />
                        </div>
                        <div data-testid='system-fleets-tab-content'>
                            <SystemFleetsTable
                                data={fleets}
                                isLoading={isLoadingFleets}
                                isFetching={isFetchingFleets}
                                isError={isErrorFleets}
                                refetch={refetchFleets}
                            />
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </main>
    );
};

export default SettingsPage;
